import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, EMPTY, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';

import { LexbeCase } from '../../../models/case.model';
import { CasesService } from '../../../services/cases.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { BrowseManager } from '../../../managers/browse.manager';
import { Router } from '@angular/router';

@Component({
    selector: 'lex-case-select',
    templateUrl: './case-select.component.html',
    styleUrls: ['./case-select.component.scss'],
    standalone: false
})
export class CaseSelectComponent implements OnInit, OnDestroy, AfterViewInit {

	@Input() currentCase: LexbeCase;
	@Input() currentCaseIsDeleted: boolean;
	@Input() currentAccountIsDeleted: boolean;
	@ViewChild('searchBox') searchBoxElement: ElementRef;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	isSearch: boolean = false;

	private searchTerm = new BehaviorSubject<string>('');
	private subscription = new Subscription();
	cases: LexbeCase[];

	searchText: string = '';
	total: number;
	pageIndex: number = 0;
	pageSize: number = 20;
	pageSizeOptions: number[] = [20, 40, 60, 80, 100];

	constructor(
		private casesService: CasesService,
		private snackBar: MatSnackBar,
		private authenticationService: AuthenticationService,
		private browseManager: BrowseManager,
		private router: Router) { }

	ngOnInit(): void {
		this.subscription.add(
			this.searchTerm.pipe(
				// Wait 300ms after each keystroke before considering the term
				debounceTime(300),
				// Ignore new term if same as previous term
				distinctUntilChanged(),
				// Switch to new search observable each time the term changes
				switchMap((term: string) => {
					this.searchText = term;
					this.updateCases();
					return EMPTY;
				})
			).subscribe());

		if (this.currentAccountIsDeleted) this.snackBar.open('The current account has been deleted. Please contact your account admin.');
		this.casesService.getCases(this.pageIndex, this.pageSize, this.searchText).subscribe((data) => {
			this.total = data.count;
			if (this.total === 0) this.snackBar.open('You do not have access to any cases. Please contact your account admin to grant you access.');
			else if (this.currentCaseIsDeleted) this.snackBar.open('The current case has been deleted. Please select a different case.');
		});
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	// Push a search term into the observable stream.
	updateSearchText(term: string): void {
		this.searchTerm.next(term);
	}

	updateCases(): void {
		this.casesService.getCases(this.pageIndex, this.pageSize, this.searchText)
			.subscribe((data) => {
				this.total = data.count;
				this.cases = data.value;
			});
	}

	setCase(myCase: LexbeCase): void {
		this.browseManager.reset();
		this.casesService.setCurrentCase(myCase.caseId).pipe(
			switchMap(() => {
				this.snackBar.dismiss();
				return this.authenticationService.refreshToken()
			})).subscribe(() => this.router.navigate(['/dashboard']));
	}

	showSearch(): void {
		this.isSearch = !this.isSearch;
		this.updateSearchText('');
		setTimeout(() => this.searchBoxElement.nativeElement.focus(), 0);
	}

	ngAfterViewInit(): void {
		this.paginator.page.subscribe((event: PageEvent) => this.getCasesPage(event));
	}

	private getCasesPage($event: PageEvent): void {
		this.pageIndex = $event.pageIndex;
		this.pageSize = $event.pageSize;
		this.updateCases();
	}
}
